import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { getDocumentSignaturePdfs } from 'api/company/getDocumentSignaturePdfs'
import { companyFilesKey } from './keys'

function isAnnotatedExtra(extra: Record<string, unknown>) {
  return Array.isArray(extra.fields) && (extra.fields as unknown[]).length > 0
}

type UseDocumentSignatureTemplatesQueryData = {
  files: Array<
    Awaited<ReturnType<typeof getDocumentSignaturePdfs>>['files'][number] & {
      isAnnotated: boolean
    }
  >
  totalRows: number
  totalPages: number
  currentPage: number
  pageSize: number
}

type UseDocumentSignatureTemplatesQueryKey = ReturnType<typeof companyFilesKey>

type UseDocumentSignatureTemplatesQueryOptions = Partial<
  UseQueryOptions<
    UseDocumentSignatureTemplatesQueryData,
    Error,
    UseDocumentSignatureTemplatesQueryData,
    UseDocumentSignatureTemplatesQueryKey
  >
>

export function useDocumentSignatureTemplatesQuery(
  companyId: number,
  params: { page?: number; pageSize?: number } = {},
  options: UseDocumentSignatureTemplatesQueryOptions = {}
) {
  const page = params.page ?? 0
  const pageSize = params.pageSize ?? 50

  return useQuery({
    ...options,
    queryKey: companyFilesKey(companyId, { page, pageSize }),
    queryFn: async (): Promise<UseDocumentSignatureTemplatesQueryData> => {
      const response = await getDocumentSignaturePdfs({
        companyId,
        page,
        pageSize,
      })
      const files = (response.files ?? []).map((file) => ({
        ...file,
        isAnnotated: isAnnotatedExtra(file.extra ?? {}),
      }))
      return {
        files,
        totalRows: response.totalRows ?? 0,
        totalPages: response.totalPages ?? 0,
        currentPage: response.currentPage ?? page,
        pageSize: response.pageSize ?? pageSize,
      }
    },
  })
}
